import { PresetTag } from '../../Tag/SoftTag';
import { dateSorter } from '@components';
import { L } from '@lib';
import { ApprovalServiceSnapshot, OrderApprovalHistory, OrderApprovalStatus } from '@services';
import { Utils } from '@utils';
import { Divider, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

const { Text } = Typography;

export const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), toCamelCase(v)])
    );
  }
  return obj;
};

const tagStyle = { margin: 0, minWidth: 100, textAlign: 'center' as const };

export const resolveStatus = (record: OrderApprovalHistory): OrderApprovalStatus => {
  if (record.status) return record.status;
  if (record.isApproved === true) return 'Approved';
  if (record.isApproved === false) return 'Declined';
  return 'Pending';
};

const STATUS_TAG: Record<OrderApprovalStatus, { color: string; label: string }> = {
  Pending: { color: 'orange', label: 'Pending' },
  Approved: { color: 'green', label: 'Approved' },
  Declined: { color: 'red', label: 'Declined' },
  Superseded: { color: 'default', label: 'Superseded' },
};

export const getStatusTag = (record: OrderApprovalHistory) => {
  const { color, label } = STATUS_TAG[resolveStatus(record)];
  return <PresetTag color={color} style={tagStyle}>{label}</PresetTag>;
};

export const Columns = () => [
  {
    title: L('Status'),
    dataIndex: 'status',
    key: 'status',
    width: 110,
    render: (_: any, record: OrderApprovalHistory) => getStatusTag(record),
  },
  {
    title: L('Sent at'),
    dataIndex: 'creationTime',
    key: 'creationTime',
    width: 150,
    ...dateSorter,
    render: (date: Date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
  },
  {
    // Consolidates "responded at / by / type" into one column to keep the header uncluttered.
    title: L('Response'),
    key: 'response',
    width: 220,
    render: (_: any, record: OrderApprovalHistory) => {
      const s = resolveStatus(record);
      if (s !== 'Approved' && s !== 'Declined') {
        return <Text type="secondary">{L('Awaiting response')}</Text>;
      }
      const date = record.lastModificationTime;
      const meta = [record.respondedByFullName, record.approvalResponseType]
        .filter(Boolean)
        .join(' · ');
      return (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span>{date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '—'}</span>
          {meta && <Text type="secondary" style={{ fontSize: 12 }}>{meta}</Text>}
        </div>
      );
    },
  },
];

const cellStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  borderBottom: '1px solid #f0f0f0',
};

const headerCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontSize: 12,
  fontWeight: 600,
  color: '#8c8c8c',
  borderBottom: '2px solid #e8ecf1',
  textTransform: 'uppercase',
};

const partCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontSize: 12,
  color: '#8c8c8c',
  backgroundColor: '#fafafa',
};

const summaryLineStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '4px 0',
};

export const ExpandedRowRender = (record: OrderApprovalHistory) => {
  if (!record.snapshot) return null;

  let services: ApprovalServiceSnapshot[];
  try {
    services = toCamelCase(JSON.parse(record.snapshot));
  } catch {
    return null;
  }

  if (!services?.length) return null;

  const labourPrice = services.reduce(
    (sum, s) =>
      sum +
      Utils.calculateLabourPrice({
        isFixed: s.isFixed,
        price: s.price || 0,
        discount: s.discount || 0,
        duration: s.duration || 1,
      }),
    0
  );

  const allExtras = services.flatMap((s) => s.extras ?? []);

  const partsPrice = allExtras.reduce((sum, e) => {
    const partTotal = (e.price || 0) * (e.quantity || 0);
    return sum + partTotal - (partTotal * (e.discount || 0)) / 100;
  }, 0);

  const subTotal = labourPrice + partsPrice;

  const serviceVat = services.reduce(
    (sum, s) =>
      sum +
      Utils.calculateVat({
        isFixed: s.isFixed,
        price: s.price || 0,
        discount: s.discount || 0,
        duration: s.duration || 1,
        vat: s.vat || 0,
      }),
    0
  );

  const partsVat = allExtras.reduce(
    (sum, e) =>
      sum +
      Utils.calculateVat({
        price: e.price || 0,
        discount: e.discount || 0,
        duration: e.quantity || 0,
        vat: e.vat || 0,
      }),
    0
  );

  const vatAmount = serviceVat + partsVat;
  const total = subTotal + vatAmount;

  return (
    <div style={{ padding: '8px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <td style={headerCellStyle}>Name</td>
            <td style={{ ...headerCellStyle, width: 70 }}>Type</td>
            <td style={{ ...headerCellStyle, width: 90, textAlign: 'right' }}>Price</td>
            <td style={{ ...headerCellStyle, width: 70, textAlign: 'right' }}>Quantity</td>
            <td style={{ ...headerCellStyle, width: 70, textAlign: 'right' }}>Discount</td>
            <td style={{ ...headerCellStyle, width: 60, textAlign: 'right' }}>VAT</td>
            <td style={{ ...headerCellStyle, width: 90, textAlign: 'right' }}>Total</td>
          </tr>
        </thead>
        <tbody>
          {services.map((s, i) => {
            const svcTotal = Utils.calculateLabourPrice({
              isFixed: s.isFixed,
              price: s.price || 0,
              discount: s.discount || 0,
              duration: s.duration || 1,
            });
            return (
              <React.Fragment key={`svc-${i}`}>
                <tr>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>
                    {s.title}
                    {s.note && (
                      <div style={{ fontSize: 11, fontWeight: 400, color: '#8c8c8c', marginTop: 2 }}>
                        {s.note}
                      </div>
                    )}
                  </td>
                  <td style={cellStyle}>{s.isFixed ? 'Fixed' : 'Labour'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{Utils.formatCurrency(s.price || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{!s.isFixed ? `${s.duration || 0}h` : '0h'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{s.discount || 0}%</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{s.vat || 0}%</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{Utils.formatCurrency(svcTotal)}</td>
                </tr>
                {s.extras?.map((e, j) => {
                  const partTotal =
                    (e.price || 0) * (e.quantity || 0) -
                    ((e.price || 0) * (e.quantity || 0) * (e.discount || 0)) / 100;
                  return (
                    <tr key={`ext-${j}`}>
                      <td style={{ ...partCellStyle, paddingLeft: 20 }}>{e.name}</td>
                      <td style={partCellStyle}>{e.type || 'Part'}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{Utils.formatCurrency(e.price || 0)}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{e.quantity || 0}</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{e.discount || 0}%</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{e.vat || 0}%</td>
                      <td style={{ ...partCellStyle, textAlign: 'right' }}>{Utils.formatCurrency(partTotal)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <Divider style={{ margin: '8px 0' }} />

      <div style={summaryLineStyle}>
        <Text>Labour</Text>
        <Text>{Utils.formatCurrency(labourPrice)}</Text>
      </div>
      <div style={summaryLineStyle}>
        <Text>Parts & Fluids</Text>
        <Text>{Utils.formatCurrency(partsPrice)}</Text>
      </div>
      <div style={summaryLineStyle}>
        <Text>VAT</Text>
        <Text>{Utils.formatCurrency(vatAmount)}</Text>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      <div style={summaryLineStyle}>
        <Text strong>TOTAL</Text>
        <Text strong>{Utils.formatCurrency(total)}</Text>
      </div>
    </div>
  );
};
